import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ZITI_URLS } from '@netfoundry-ui/shared/services';

/** One source address from the selection, plus the identity we would mint for it. */
export interface SourceIdentityRow {
    /** The observed source IP or CIDR. */
    address: string;
    /** Proposed name for the minted identity (editable). */
    name: string;
    /** Whether to create an identity for this source. */
    include: boolean;
}

/**
 * "Create identities per source address" - a step in the create-from-traffic flow. The operator reviews
 * the source addresses seen in the selection and mints a Ziti identity for each, all tagged with one
 * shared role attribute. The build step that follows grants that role attribute on the service's dial
 * policy, so the minted identities can reach the new service. Enrollment tokens are surfaced to download.
 */
@Component({
    selector: 'app-source-identities',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './source-identities.component.html',
    styleUrls: ['./source-identities.component.scss'],
})
export class SourceIdentitiesComponent implements OnInit {
    rows: SourceIdentityRow[] = [];
    /** The shared role attribute every minted identity carries (stored bare, shown with a leading #). */
    roleAttribute = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.rows = this.sourceAddresses().map((address) => ({
            address,
            name: this.defaultName(address),
            include: true,
        }));
        this.roleAttribute = this.defaultRoleAttribute();
    }

    /** The service name(s) these identities will be granted access to, shown as context. Empty until the
     *  step runs after service definition and the build step passes them. */
    get grantedServices(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('svc'));
    }

    get includedCount(): number {
        return this.rows.filter((r) => r.include).length;
    }

    get allIncluded(): boolean {
        return this.rows.length > 0 && this.rows.every((r) => r.include);
    }

    toggleAll(): void {
        const next = !this.allIncluded;
        this.rows.forEach((r) => (r.include = next));
    }

    get canContinue(): boolean {
        return this.includedCount > 0 && !!this.roleAttribute.trim();
    }

    /** The source IPs observed in the selection; "(empty)" is the placeholder for identity initiators. */
    private sourceAddresses(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('srcIp')).filter((ip) => ip !== '(empty)');
    }

    /** A readable default identity name from an address: "10.0.0.1" -> "src-10-0-0-1". */
    private defaultName(address: string): string {
        const slug = address
            .trim()
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return slug ? `src-${slug}` : 'src';
    }

    /** A default role attribute from the selection label, else a generic one. */
    private defaultRoleAttribute(): string {
        const label = this.route.snapshot.queryParamMap.get('label') || '';
        const slug = label
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return slug ? `${slug}-sources` : 'traffic-sources';
    }

    private splitParam(value: string | null): string[] {
        return (value || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }

    /** This is an optional step reached after the services are created, so every exit returns to the
     *  Services list rather than back into the build flow. */
    back(): void {
        this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICES);
    }

    /** Leave without minting any identities. */
    skip(): void {
        this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICES);
    }

    create(): void {
        if (!this.canContinue) {
            return;
        }
        // TODO (next increment): mint an identity per included row (ott enrollment), tag each with the
        // shared role attribute, patch the created services' dial policies to grant it, then surface the
        // enrollment JWTs to download. For now this just returns to the Services list.
        this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICES);
    }
}
